import { createClerkClient } from '@clerk/backend';
import type { RequestState } from '@clerk/backend/internal';
import { AuthStatus, createClerkRequest } from '@clerk/backend/internal';
import { clerkFrontendApiProxy, DEFAULT_PROXY_PATH, stripTrailingSlashes } from '@clerk/backend/proxy';
import { isDevelopmentFromSecretKey } from '@clerk/shared/keys';
import { logger } from '@clerk/shared/logger';
import { isHttpOrHttps, isProxyUrlRelative, isValidProxyUrl } from '@clerk/shared/proxy';
import { handleValueOrFn } from '@clerk/shared/utils';
import type { RequestHandler, Response } from 'express';
import { Readable } from 'stream';

import { clerkClient as defaultClerkClient } from './clerkClient';
import { satelliteAndMissingProxyUrlAndDomain, satelliteAndMissingSignInUrl } from './errors';
import type { AuthenticateRequestParams, ClerkMiddlewareOptions } from './types';
import {
  brandRequestAuth,
  incomingMessageToRequest,
  loadApiEnv,
  loadClientEnv,
  requestHasAuthObject,
  requestToProxyRequest,
} from './utils';

/**
 * @internal
 * Authenticates an Express request by wrapping clerkClient.authenticateRequest and
 * converts the express request object into a standard web request object
 *
 * @param opts - Configuration options for request authentication
 * @param opts.clerkClient - The Clerk client instance to use for authentication
 * @param opts.request - The Express request object to authenticate
 * @param opts.options - Optional middleware configuration options
 */
export const authenticateRequest = (opts: AuthenticateRequestParams) => {
  const { clerkClient, request, options } = opts;
  // Peel off middleware-only keys and the few options that need middleware-side
  // resolution (env fallbacks, URL normalization). Everything else is spread
  // straight through, so new AuthenticateRequestOptions/VerifyTokenOptions
  // fields flow to the backend without another code change here.
  const {
    clerkClient: _clerkClient,
    debug: _debug,
    frontendApiProxy: _frontendApiProxy,
    isSatellite: isSatelliteInput,
    domain: domainInput,
    signInUrl: signInUrlInput,
    proxyUrl: proxyUrlInput,
    secretKey: secretKeyInput,
    machineSecretKey: machineSecretKeyInput,
    publishableKey: publishableKeyInput,
    ...restOptions
  } = options || {};

  const clerkRequest = createClerkRequest(incomingMessageToRequest(request));
  const env = { ...loadApiEnv(), ...loadClientEnv() };

  const secretKey = secretKeyInput || env.secretKey;
  const machineSecretKey = machineSecretKeyInput || env.machineSecretKey;
  const publishableKey = publishableKeyInput || env.publishableKey;

  const isSatellite = handleValueOrFn(isSatelliteInput, clerkRequest.clerkUrl, env.isSatellite);
  const domain = handleValueOrFn(domainInput, clerkRequest.clerkUrl) || env.domain;
  const signInUrl = signInUrlInput || env.signInUrl;
  const proxyUrl = absoluteProxyUrl(
    handleValueOrFn(proxyUrlInput, clerkRequest.clerkUrl, env.proxyUrl),
    clerkRequest.clerkUrl.toString(),
  );

  if (isSatellite && !proxyUrl && !domain) {
    throw new Error(satelliteAndMissingProxyUrlAndDomain);
  }

  if (isSatellite && !isHttpOrHttps(signInUrl) && isDevelopmentFromSecretKey(secretKey || '')) {
    throw new Error(satelliteAndMissingSignInUrl);
  }

  return clerkClient.authenticateRequest(clerkRequest, {
    ...restOptions,
    secretKey,
    machineSecretKey,
    publishableKey,
    proxyUrl,
    isSatellite,
    domain,
    signInUrl,
  });
};

const setResponseHeaders = (requestState: RequestState, res: Response): Error | undefined => {
  if (requestState.headers) {
    requestState.headers.forEach((value, key) => res.appendHeader(key, value));
  }
  return setResponseForHandshake(requestState, res);
};

/**
 * Depending on the auth state of the request, handles applying redirects and validating that a handshake state was properly handled.
 *
 * Returns an error if state is handshake without a redirect, otherwise returns undefined. res.writableEnded should be checked after this method is called.
 */
const setResponseForHandshake = (requestState: RequestState, res: Response): Error | undefined => {
  const hasLocationHeader = requestState.headers.get('location');
  if (hasLocationHeader) {
    // triggering a handshake redirect
    res.status(307).end();
    return;
  }

  if (requestState.status === AuthStatus.Handshake) {
    return new Error('Clerk: unexpected handshake without redirect');
  }

  return;
};

const absoluteProxyUrl = (relativeOrAbsoluteUrl: string, baseUrl: string): string => {
  if (!relativeOrAbsoluteUrl || !isValidProxyUrl(relativeOrAbsoluteUrl) || !isProxyUrlRelative(relativeOrAbsoluteUrl)) {
    return relativeOrAbsoluteUrl;
  }
  return new URL(relativeOrAbsoluteUrl, baseUrl).toString();
};

// `apiUrl` and `apiVersion` are pinned at client construction time inside
// `@clerk/backend`'s `createAuthenticateRequest` factory (build-time values
// override runtime ones). The default singleton in `./clerkClient` is built
// from env only, so passing these via `clerkMiddleware()` would be silently
// ignored. When the caller hasn't supplied their own `clerkClient` but did
// pass `apiUrl`/`apiVersion`, build a per-middleware client with those values.
const resolveDefaultClerkClient = (options: ClerkMiddlewareOptions) => {
  if (!options.apiUrl && !options.apiVersion) {
    return defaultClerkClient;
  }
  const env = { ...loadApiEnv(), ...loadClientEnv() };
  return createClerkClient({
    ...env,
    ...(options.apiUrl ? { apiUrl: options.apiUrl } : {}),
    ...(options.apiVersion ? { apiVersion: options.apiVersion } : {}),
    userAgent: `${PACKAGE_NAME}@${PACKAGE_VERSION}`,
  });
};

export const authenticateAndDecorateRequest = (options: ClerkMiddlewareOptions = {}): RequestHandler => {
  const clerkClient = options.clerkClient || resolveDefaultClerkClient(options);

  // Extract proxy configuration
  const frontendApiProxy = options.frontendApiProxy;
  const proxyPath = stripTrailingSlashes(frontendApiProxy?.path ?? DEFAULT_PROXY_PATH) || DEFAULT_PROXY_PATH;

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const middleware: RequestHandler = async (request, response, next) => {
    // Skip authentication only when a Clerk middleware has already processed
    // this request. The brand check matters: a truthy `req.auth` is not proof
    // of that, since express-jwt, passport and other libraries set the same
    // property, and treating their value as ours would silently disable Clerk
    // authentication.
    if (requestHasAuthObject(request)) {
      return next();
    }

    if ('auth' in request) {
      logger.warnOnce(
        'Clerk: another middleware has already set `req.auth` on this request. Clerk authentication will run anyway and overwrite it. To use another auth library alongside Clerk, configure it to store its state on a different request property.',
      );
    }

    const env = { ...loadApiEnv(), ...loadClientEnv() };
    const publishableKey = options.publishableKey || env.publishableKey;
    const secretKey = options.secretKey || env.secretKey;

    // Handle Frontend API proxy requests early, before authentication
    if (frontendApiProxy) {
      const requestUrl = new URL(request.originalUrl || request.url, `http://${request.headers.host}`);
      const isEnabled =
        typeof frontendApiProxy.enabled === 'function'
          ? frontendApiProxy.enabled(requestUrl)
          : frontendApiProxy.enabled;

      if (isEnabled && (requestUrl.pathname === proxyPath || requestUrl.pathname.startsWith(proxyPath + '/'))) {
        // Convert Express request to Fetch API Request
        const proxyRequest = requestToProxyRequest(request);

        // Call the core proxy function
        const proxyResponse = await clerkFrontendApiProxy(proxyRequest, {
          proxyPath,
          publishableKey,
          secretKey,
        });

        // Send the proxy response back to the client
        response.status(proxyResponse.status);
        proxyResponse.headers.forEach((value, key) => {
          response.setHeader(key, value);
        });

        if (proxyResponse.body) {
          const reader = proxyResponse.body.getReader();
          const stream = new Readable({
            async read() {
              try {
                const { done, value } = await reader.read();
                if (done) {
                  this.push(null);
                } else {
                  this.push(Buffer.from(value));
                }
              } catch (error) {
                this.destroy(error instanceof Error ? error : new Error(String(error)));
              }
            },
          });
          stream.pipe(response);
        } else {
          response.end();
        }
        return;
      }
    }

    // Pass the proxy path to authenticateRequest - the backend resolves it
    // against the request's public origin (from x-forwarded-* headers).
    let resolvedOptions = options;
    if (frontendApiProxy && !options.proxyUrl) {
      const requestUrl = new URL(request.originalUrl || request.url, `http://${request.headers.host}`);
      const isProxyEnabled =
        typeof frontendApiProxy.enabled === 'function'
          ? frontendApiProxy.enabled(requestUrl)
          : frontendApiProxy.enabled;
      if (isProxyEnabled) {
        resolvedOptions = { ...options, proxyUrl: proxyPath };
      }
    }

    try {
      const requestState = await authenticateRequest({
        clerkClient,
        request,
        options: resolvedOptions,
      });

      const err = setResponseHeaders(requestState, response);
      if (err) {
        return next(err);
      }
      if (response.writableEnded) {
        return;
      }

      const auth = brandRequestAuth((opts: Parameters<typeof requestState.toAuth>[0]) => requestState.toAuth(opts));

      Object.assign(request, { auth });

      next();
    } catch (err) {
      next(err);
    }
  };

  return middleware;
};
